import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { SigningField } from '../types/fields';
import {
  pdfToViewerCoords,
  clampFieldToPage,
  computeResizeDimensions,
  viewerToPdfCoords,
  resolveDisplayDimensions,
  resolveViewerScales,
} from '../utils/coordinates';
import { containImageInBoxClient } from '../utils/imageFit';
import { fieldTextLayoutMode, isSingleLineFormText } from '../utils/textLayout';
import { FieldTextPreview } from '../utils/fieldTextPreview';
import { isTextBasedFieldType, resolveFieldTextStyle, cssFontStack, toPreviewCss } from '../utils/fontMapping';
import { InlineFieldEditor, isInlineEditableField, type EditorScreenBox } from './InlineFieldEditor';
import { FloatingTextFormatToolbar } from './TextFormatToolbar';
import {
  effectiveScrollDismissedFieldId,
  shouldShowFloatingToolbar,
} from '../lib/floatingToolbarVisibility';
import type { FloatingToolbarScrollDismissState } from '../lib/floatingToolbarScrollDismiss';
import {
  armScrollDismissState,
  createScrollDismissState,
  evaluateFloatingToolbarScrollDismiss,
  FLOATING_TOOLBAR_SCROLL_DISMISS_GRACE_MS,
} from '../lib/floatingToolbarScrollDismiss';
import { buildTextFormatUpdates, type TextFormatPatch } from '../utils/textFormatActions';
import {
  cycleFieldSelectionAtPdfCenter,
  getFieldsAtPdfPoint,
  getLastOverlapStackKey,
  overlapStackKey,
  selectFieldAtPoint,
  resetAllOverlapStackKeys,
  setLastOverlapStackKey,
} from '../utils/fieldSelection';

// Worker is configured once early in main.tsx. This component only needs the
// pdfjsLib namespace for runtime calls (getPage, render, etc.).

interface PdfPageProps {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageIndex: number;
  scale: number;
  fields: SigningField[];
  selectedFieldId: string | null;
  isPlacing: boolean;
  placementType: SigningField['type'] | null;
  onPlaceField: (pageIndex: number, pdfX: number, pdfY: number, renderScale: number) => void;
  onFieldUpdate: (id: string, updates: Partial<SigningField>) => void;
  onFieldSelect: (id: string | null) => void;
  onFieldDelete: (id: string) => void;
  pageWidth: number;   // PDF points
  pageHeight: number;  // PDF points
  isLoadingSizes?: boolean;
  /** Optional: report a thumbnail when this page renders (used by PageNavigator for reuse) */
  onThumbnailUpdate?: (pageIndex: number, dataUrl: string) => void;
  /** Report pdf.js viewport dimensions (scale 1) after this page renders — authoritative visual size. */
  onPageViewportMeasured?: (
    pageIndex: number,
    size: { width: number; height: number; rotation?: number }
  ) => void;
  /** When false, hide all placed fields so the original PDF is visible (high-impact compare mode) */
  showPlacedFields?: boolean;
  /** Optional ghost fields from a snapshot for side-by-side visual comparison */
  compareFields?: any[] | null;
  /**
   * Pre-computed Map<id, {status}> for ghost fields. Built once in PdfViewer so each
   * page does an O(1) Map lookup instead of an O(N) .find() per ghost.
   */
  ghostStatusById?: ReadonlyMap<string, { status: 'added' | 'removed' | 'changed' | 'unchanged' }>;
  /** Controllable opacity for the ghost overlay (0.2–0.8). Controlled from viewer. */
  compareOpacity?: number;
  /** Global pan mode - when true, disable field interactions so drag pans the document */
  panMode?: boolean;
  inlineEditingFieldId?: string | null;
  onStartInlineEdit?: (fieldId: string) => void;
  onCommitInlineEdit?: (fieldId: string, value: string, editorBox: EditorScreenBox) => void;
  onCancelInlineEdit?: () => void;
  inlineEditDismissRef?: React.MutableRefObject<boolean>;
  /** When true, always render even if off-screen (current visible page). */
  isCurrentPage?: boolean;
  /**
   * Called when the user completes a freehand ink stroke while the 'ink' tool is active.
   * points are already in PDF coordinate space (bottom-left origin).
   * The wiring agent implements this to addField an 'ink' type field.
   */
  onPlaceInk?: (pageIndex: number, points: Array<{ x: number; y: number }>, renderScale: number) => void;
}

const PdfPageComponent: React.FC<PdfPageProps> = ({
  pdfDoc,
  pageIndex,
  scale,
  fields,
  selectedFieldId,
  isPlacing,
  placementType,
  onPlaceField,
  onFieldUpdate,
  onFieldSelect,
  onFieldDelete,
  pageWidth,
  pageHeight,
  isLoadingSizes = false,
  onThumbnailUpdate,
  onPageViewportMeasured,
  showPlacedFields = true,
  compareFields = null,
  ghostStatusById,
  compareOpacity = 0.45,
  panMode = false,
  inlineEditingFieldId = null,
  onStartInlineEdit,
  onCommitInlineEdit,
  onCancelInlineEdit,
  inlineEditDismissRef,
  isCurrentPage = false,
  onPlaceInk,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // FIX-1: ref shared between handleFieldPointerDown and onClick so the click
  // handler can skip the checkbox toggle when a real drag occurred. A closure-local
  // `dragging` variable is invisible across JSX event-handler closures.
  const didDragRef = useRef(false);
  const pointerDownFieldIdRef = useRef<string | null>(null);
  const onThumbnailUpdateRef = useRef(onThumbnailUpdate);
  useEffect(() => {
    onThumbnailUpdateRef.current = onThumbnailUpdate;
  }, [onThumbnailUpdate]);
  /** Shared box for canvas + overlay so CSS scaling affects both equally. */
  const surfaceRef = useRef<HTMLDivElement>(null);
  /** Measured after layout (useLayoutEffect) — safe for coord math after zoom. */
  const [layoutSize, setLayoutSize] = useState<{ width: number; height: number } | null>(null);
  /** Committed layout box (offsetWidth/Height) — distinguishes CSS shrink from stale zoom cache. */
  const [committedSize, setCommittedSize] = useState<{ width: number; height: number } | null>(
    null
  );
  /** pdf.js viewport at scale 1 — authoritative for overlay math on rotated pages. */
  const [measuredViewport, setMeasuredViewport] = useState<{
    width: number;
    height: number;
    rotation?: number;
  } | null>(null);

  const overlayPageWidth = measuredViewport?.width ?? pageWidth;
  const overlayPageHeight = measuredViewport?.height ?? pageHeight;
  const compareActive = !!(compareFields && compareFields.length > 0);

  const nominalWidth = overlayPageWidth * scale;
  const nominalHeight = overlayPageHeight * scale;

  const { width: displayWidth, height: displayHeight } = resolveDisplayDimensions(
    nominalWidth,
    nominalHeight,
    layoutSize,
    committedSize?.width,
    committedSize?.height
  );
  const { scaleX: effectiveScaleX, scaleY: effectiveScaleY } = resolveViewerScales(
    overlayPageWidth,
    overlayPageHeight,
    scale,
    displayWidth,
    displayHeight
  );

  // SINGLE SOURCE OF TRUTH FOR RENDER SCALE:
  // We deliberately removed the previous internal `renderScale` useState.
  // The `scale` prop (passed from PdfViewer, ultimately controlled by the parent App)
  // is now the sole source of truth used for:
  //   - pdfjs viewport creation in the render effect
  //   - all coordinate conversions (pdfToViewerCoords / viewerToPdfCoords)
  //   - field screen rects for display
  //   - drag and resize delta calculations
  //   - font scaling
  //   - the scale value passed to onPlaceField (for getDefaultFieldSize in parent)
  //
  // Why this eliminates drift:
  // - The async render effect has `scale` in its dependency array and ALWAYS calls
  //   page.getViewport({ scale }) + page.render with the *current* prop value.
  // - Previous useState(scale) only captured the value at mount time; subsequent prop
  //   changes did not update it until after the async setRenderScale inside the effect.
  // - Placement already used the prop; display/drag used the state → tiny mismatches
  //   possible on mount, during re-renders, or if scale ever became dynamic.
  // - Using the prop directly guarantees placement, positioning, and interaction math
  //   are identical at every instant. The parent is the authority on desired render scale.
  //
  // If a future zoom feature requires per-page or async-captured scale, we can
  // re-introduce a derived/captured value in a controlled way, but for the current
  // architecture (fixed scale from parent) this is the correct minimal design.
  // Resize lifecycle state. We keep the setter (cleared on pointer-up) but never read the
  // value directly — resize math is driven entirely by values captured at pointer-down.
  const [, setResizing] = useState<{ fieldId: string; startW: number; startH: number; startX: number; startY: number } | null>(null);


  // === HIGH-IMPACT VIRTUALIZATION ===
  // Only perform expensive pdfjs canvas rendering + full field overlay when the page
  // is near the viewport. This is the #1 performance win for large documents (30+ pages).
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const element = document.getElementById(`pdf-page-${pageIndex}`);
    if (!element) return;

    // Must observe against the document scroll container (.editor-stage-viewer),
    // NOT the browser viewport. With root:null, pages clipped by overflow:auto
    // still intersect the viewport and stay "active" — defeating virtualization
    // on multi-page PDFs (12+ pages render far more canvases than intended).
    const scrollRoot = document.querySelector('.editor-stage-viewer');
    if (!scrollRoot) return;

    // Buffer of ~2 pages above and below for smooth scrolling experience
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsActive(entry.isIntersecting);
      },
      {
        root: scrollRoot,
        rootMargin: '100% 0px 100% 0px',
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [pageIndex]);

  const isEditingOnThisPage =
    inlineEditingFieldId != null &&
    fields.some((f) => f.pageIndex === pageIndex && f.id === inlineEditingFieldId);
  const effectiveActive = isActive || isCurrentPage || isEditingOnThisPage;

  const measureSurface = useCallback(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const rect = surface.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const committed = { width: surface.offsetWidth, height: surface.offsetHeight };
    setCommittedSize(committed);

    // Ignore stale pre-layout reads during zoom transitions: the committed box
    // is already at nominal but getBoundingClientRect can lag one frame behind.
    if (
      rect.width < nominalWidth - 1.5 &&
      rect.height < nominalHeight - 1.5 &&
      committed.width >= nominalWidth - 1.5 &&
      committed.height >= nominalHeight - 1.5
    ) {
      return;
    }

    setLayoutSize({ width: rect.width, height: rect.height });
  }, [nominalWidth, nominalHeight]);

  // After zoom, assume nominal bitmap size first; ResizeObserver picks up live size.
  useLayoutEffect(() => {
    if (!effectiveActive) return;
    setLayoutSize({ width: nominalWidth, height: nominalHeight });
    const surface = surfaceRef.current;
    if (surface) {
      setCommittedSize({ width: surface.offsetWidth, height: surface.offsetHeight });
    }
  }, [effectiveActive, nominalWidth, nominalHeight]);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !effectiveActive) return;

    measureSurface();
    const ro = new ResizeObserver(measureSurface);
    ro.observe(surface);
    return () => ro.disconnect();
  }, [effectiveActive, measureSurface, nominalWidth, nominalHeight]);

  // Render this specific page to canvas ONLY when active (virtualization)
  useEffect(() => {
    if (!effectiveActive) {
      // Free memory when page goes off-screen
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      return;
    }

    let cancelled = false;
    let renderTask: { promise: Promise<void>; cancel: () => void } | null = null;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(pageIndex + 1); // pdfjs is 1-based
        if (cancelled) return;

        const rotation = (page as { rotate?: number }).rotate ?? 0;
        const measureViewport = page.getViewport({ scale: 1.0 });
        const measured = {
          width: measureViewport.width,
          height: measureViewport.height,
          rotation,
        };
        setMeasuredViewport(measured);
        onPageViewportMeasured?.(pageIndex, measured);

        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;

        // FIX-3: HiDPI / Retina support. Scale the canvas backing store by
        // devicePixelRatio so the bitmap is rendered at physical pixel density.
        // The CSS width/height stays at CSS px (viewport.width/height) via the
        // canvas className — only the internal resolution is boosted.
        // Guard for dpr=1 (no-op) and environments where dpr may be undefined.
        const dpr = (typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1);

        const ctx = canvas.getContext('2d', { alpha: false })!;
        // Set the backing-store size to physical pixels.
        canvas.width = Math.round(viewport.width * dpr);
        canvas.height = Math.round(viewport.height * dpr);
        // Keep CSS size at logical CSS px so layout and overlay math are unaffected.
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Scale the drawing context so pdf.js renders at full physical resolution.
        if (dpr !== 1) ctx.scale(dpr, dpr);

        // Keep the task so we can cancel it if scale changes or the page scrolls
        // off-screen mid-render. Without this, re-running render on the same canvas
        // throws "Cannot use the same canvas during multiple render() operations".
        renderTask = page.render({
          canvasContext: ctx,
          viewport,
        });
        await renderTask.promise;
        if (cancelled) return;

        // Report a small thumbnail to the navigator for reuse (high-impact optimization)
        const reportThumbnail = onThumbnailUpdateRef.current;
        if (reportThumbnail) {
          try {
            const thumbCanvas = document.createElement('canvas');
            const tctx = thumbCanvas.getContext('2d', { alpha: false })!;
            const thumbScale = 0.15;
            thumbCanvas.width = viewport.width * thumbScale;
            thumbCanvas.height = viewport.height * thumbScale;

            tctx.drawImage(
              canvas,
              0, 0, canvas.width, canvas.height,
              0, 0, thumbCanvas.width, thumbCanvas.height
            );

            const dataUrl = thumbCanvas.toDataURL('image/jpeg', 0.7);
            reportThumbnail(pageIndex, dataUrl);

            // cleanup
            thumbCanvas.width = 0;
            thumbCanvas.height = 0;
          } catch {
            // non-critical
          }
        }

        // No setRenderScale needed anymore: we use the `scale` prop directly everywhere.
      } catch (err: any) {
        // Cancellation is expected when scale changes or the page scrolls off-screen
        // mid-render; don't surface it as an error.
        if (cancelled || err?.name === 'RenderingCancelledException') return;
        console.error('Failed to render page', pageIndex, err);
      }
    };

    renderPage();
    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDoc, pageIndex, scale, effectiveActive, onPageViewportMeasured]);

  const pageFields = useMemo(
    () => fields.filter((f) => f.pageIndex === pageIndex),
    [fields, pageIndex]
  );

  // Convert PDF stored coords → screen pixels for absolutely positioned fields
  // Uses the prop `scale` (single source of truth) so displayed field rects are
  // always computed with the exact same value that will be / was used to render
  // the underlying pdfjs canvas.
  const getFieldScreenRect = useCallback((field: SigningField) => {
    return pdfToViewerCoords(
      field,
      overlayPageHeight,
      scale,
      overlayPageWidth,
      displayWidth,
      displayHeight
    );
  }, [overlayPageHeight, overlayPageWidth, scale, displayWidth, displayHeight]);

  // Handle click on the page overlay to place a new field
  //
  // COORDINATE HARDENING NOTE (post-renderScale removal):
  // Placement, display, drag, and resize now *all* use the exact same `scale` value
  // (the prop). This is the authoritative render scale decided by the parent.
  //
  // We convert HERE using the EXACT pageHeight (from pdfjs viewport at scale:1.0 in PdfViewer)
  // + the current `scale` prop. This (and the earlier removal of hardcoded 792/1.45)
  // guarantees correctness for any page size and eliminates all previous drift.
  //
  // The emitted pdfX/pdfY are already in correct PDF coordinate space (y from bottom).
  // The 4th argument (typed as renderScale in the callback) is the *same* `scale` prop
  // value used for everything else on this page, so getDefaultFieldSize in the parent
  // produces fields whose on-screen size exactly matches the render scale.
  useEffect(() => {
    (window as Window & { __resetFieldOverlapStacks?: () => void }).__resetFieldOverlapStacks =
      resetAllOverlapStackKeys;
    return () => {
      delete (window as Window & { __resetFieldOverlapStacks?: () => void }).__resetFieldOverlapStacks;
    };
  }, []);

  const resolveSelectionAtViewerPoint = useCallback(
    (viewerX: number, viewerY: number, clickedFieldId?: string) => {
      // PDF-space hit test matches Tab cycling and avoids screen-rect drift on stacked fields.
      const pdfCoords = viewerToPdfCoords(
        viewerX,
        viewerY,
        overlayPageHeight,
        scale,
        overlayPageWidth,
        displayWidth,
        displayHeight
      );
      let hits = getFieldsAtPdfPoint(
        pageFields,
        pageIndex,
        pdfCoords.x,
        pdfCoords.y
      );

      const selected = selectedFieldId
        ? pageFields.find((f) => f.id === selectedFieldId)
        : null;

      // When the click touches the selected field, resolve the full overlap stack from
      // its PDF center (same as Tab) so repeat clicks can cycle buried fields.
      if (selected && hits.some((f) => f.id === selectedFieldId)) {
        const centerHits = getFieldsAtPdfPoint(
          pageFields,
          pageIndex,
          selected.x + selected.width / 2,
          selected.y + selected.height / 2
        );
        if (centerHits.length > 1) {
          hits = centerHits;
        }
      }

      if (hits.length === 0) {
        setLastOverlapStackKey(pageIndex, null);
        onFieldSelect(null);
        return { selectedId: null, cycled: false };
      }

      const stackKey = overlapStackKey(hits);
      const topmost = hits[hits.length - 1];
      const prevStackKey = getLastOverlapStackKey(pageIndex);

      // Second click on the same overlap stack while the top field is selected → cycle.
      if (
        hits.length > 1 &&
        selectedFieldId === topmost.id &&
        prevStackKey === stackKey
      ) {
        const nextId = cycleFieldSelectionAtPdfCenter(pageFields, topmost);
        if (nextId && nextId !== selectedFieldId) {
          setLastOverlapStackKey(pageIndex, stackKey);
          onFieldSelect(nextId);
          return { selectedId: nextId, cycled: true };
        }
      }

      // Pointer-down on a specific field keeps that field selected when it is part of
      // the overlap stack (e.g. after cycling to a buried field, drag must not re-pick top).
      if (clickedFieldId && hits.some((f) => f.id === clickedFieldId)) {
        setLastOverlapStackKey(pageIndex, hits.length > 1 ? stackKey : null);
        onFieldSelect(clickedFieldId);
        return { selectedId: clickedFieldId, cycled: false };
      }

      const { selectedId, clickPoint, cycled } = selectFieldAtPoint(
        hits,
        viewerX,
        viewerY,
        selectedFieldId
      );
      setLastOverlapStackKey(pageIndex, hits.length > 1 ? stackKey : null);
      if (selectedId) {
        onFieldSelect(selectedId);
      } else {
        onFieldSelect(null);
      }
      return { selectedId, cycled };
    },
    [
      pageFields,
      pageIndex,
      getFieldScreenRect,
      selectedFieldId,
      onFieldSelect,
      overlayPageHeight,
      overlayPageWidth,
      scale,
      displayWidth,
      displayHeight,
    ]
  );

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only react to clicks on the blank overlay itself, never to clicks that bubbled up
    // from a placed field — otherwise selecting a field would immediately clear it.
    if (e.target !== e.currentTarget) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const viewerX = e.clientX - rect.left;
    const viewerY = e.clientY - rect.top;

    // When not actively placing, hit-test stacked fields at the click point.
    if (!isPlacing || !placementType) {
      resolveSelectionAtViewerPoint(viewerX, viewerY);
      return;
    }

    // 'ink' strokes are captured via onPointerDown/Move/Up — skip the click-to-place flow.
    if (placementType === 'ink') return;

    // Convert using real per-page values (no more guessing Letter size!)
    const pdfCoords = viewerToPdfCoords(
      viewerX,
      viewerY,
      overlayPageHeight,
      scale,
      overlayPageWidth,
      displayWidth,
      displayHeight
    );
    onPlaceField(pageIndex, pdfCoords.x, pdfCoords.y, effectiveScaleX);
  };

  const handleFieldDoubleClick = (field: SigningField) => {
    if (isInlineEditableField(field) && onStartInlineEdit) {
      onStartInlineEdit(field.id);
    }
  };

  const DRAG_THRESHOLD_PX = 4;

  // === DRAG LOGIC ===
  const handleFieldPointerDown = (e: React.PointerEvent, field: SigningField) => {
    if (isPlacing) return;
    e.stopPropagation();

    // FIX-1: reset drag flag on every new pointer-down so onClick can check it.
    didDragRef.current = false;
    pointerDownFieldIdRef.current = field.id;

    const overlayRect = overlayRef.current?.getBoundingClientRect();
    if (!overlayRect) return;

    const viewerX = e.clientX - overlayRect.left;
    const viewerY = e.clientY - overlayRect.top;
    const { selectedId, cycled } = resolveSelectionAtViewerPoint(viewerX, viewerY, field.id);
    if (cycled || !selectedId) return;

    const dragField = pageFields.find((f) => f.id === selectedId) ?? field;

    const rect = overlayRect;
    const startX = viewerX;
    const startY = viewerY;
    const pointerOriginX = e.clientX;
    const pointerOriginY = e.clientY;
    let dragging = false;

    // Capture the pointer's offset within the field at drag start as plain consts.
    // (Previously this used dragOffset React state, which was still {0,0} during the
    // first pointermove because setState is async — causing the field to jump.)
    const fieldScreenRect = getFieldScreenRect(dragField);
    const offsetX = startX - fieldScreenRect.left;
    const offsetY = startY - fieldScreenRect.top;

    // Coalesce drag updates to at most one React commit per animation frame.
    // pointermove can fire 60-120+x/sec; without this each event would call
    // onFieldUpdate -> fields.updateField -> setFields(new array), re-rendering
    // every memoized PdfPage and re-running the compare-diff useMemo per event.
    // We compute the latest field synchronously (cheap), stash it in a closure
    // var, and flush at most once per frame. The final position is force-flushed
    // on pointerup so the committed value always matches where the pointer landed.
    let pendingUpdate: SigningField | null = null;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (!pendingUpdate) return;
      const next = pendingUpdate;
      pendingUpdate = null;
      onFieldUpdate(next.id, next);
    };

    const handleMove = (moveEvent: PointerEvent) => {
      if (!dragging) {
        const dx = Math.abs(moveEvent.clientX - pointerOriginX);
        const dy = Math.abs(moveEvent.clientY - pointerOriginY);
        if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
        dragging = true;
        // FIX-1: inform the shared ref so onClick can skip the checkbox toggle.
        didDragRef.current = true;
      }

      const overlayRect = overlayRef.current?.getBoundingClientRect();
      if (!overlayRect) return;

      const vx = moveEvent.clientX - overlayRect.left - offsetX;
      const vy = moveEvent.clientY - overlayRect.top - offsetY;

      // Convert back to PDF coords using the *same* `scale` prop used for
      // rendering the canvas and for getFieldScreenRect. Guarantees zero drift.
      const pdfX = vx / effectiveScaleX;
      const pdfY = overlayPageHeight - vy / effectiveScaleY - dragField.height;

      pendingUpdate = clampFieldToPage(
        { ...dragField, x: pdfX, y: pdfY },
        overlayPageWidth,
        overlayPageHeight
      );

      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      // Flush any update scheduled but not yet committed so the final position
      // is never dropped (e.g. last move landed between frames).
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      flush();
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  // === RESIZE LOGIC ===
  const handleResizePointerDown = (e: React.PointerEvent, field: SigningField) => {
    e.stopPropagation();
    onFieldSelect(field.id);

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = field.width;
    const startH = field.height;
    const lockAspectRatio = Boolean(field.imageDataUrl);

    setResizing({
      fieldId: field.id,
      startW,
      startH,
      startX,
      startY,
    });

    let pendingUpdate: SigningField | null = null;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (!pendingUpdate) return;
      const next = pendingUpdate;
      pendingUpdate = null;
      onFieldUpdate(next.id, next);
    };

    const handleMove = (moveEvent: PointerEvent) => {
      // Use the prop `scale` (identical value used for canvas + all other math)
      const dx = (moveEvent.clientX - startX) / effectiveScaleX;
      const dy = (moveEvent.clientY - startY) / effectiveScaleY;

      const minH = field.type === 'checkbox' ? 16 : 18;
      const { width: newW, height: newH } = computeResizeDimensions({
        startWidth: startW,
        startHeight: startH,
        dx,
        dy,
        lockAspectRatio: lockAspectRatio && !moveEvent.shiftKey,
        minWidth: 18,
        minHeight: minH,
      });

      // Anchor the field's TOP edge during resize. In PDF space y is the BOTTOM
      // edge and the on-screen top is (pageHeight - y - height) * scaleY, so the
      // PDF-space top is (y + height). Keep that constant while height changes,
      // then recompute y from the new height. This makes the bottom-right handle
      // grow the box down-and-right toward the cursor (standard resize UX),
      // instead of pinning the bottom and ballooning upward.
      const pdfTop = field.y + field.height;
      const newY = pdfTop - newH;

      pendingUpdate = clampFieldToPage(
        { ...field, y: newY, width: newW, height: newH },
        overlayPageWidth,
        overlayPageHeight
      );

      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    };

    const handleUp = () => {
      setResizing(null);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      flush();
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const handleFieldDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onFieldDelete(id);
  };

  // === INK DRAG LOGIC ===
  // When placementType === 'ink', capture pointer movement as a freehand stroke.
  // We collect viewer-px points during the drag, then convert to PDF coords on pointerup.
  const inkPointsRef = useRef<Array<{ x: number; y: number }>>([]);
  const inkDrawingRef = useRef(false);

  const appendInkPoint = useCallback((clientX: number, clientY: number) => {
    if (!inkDrawingRef.current) return;
    const overlayEl = overlayRef.current;
    if (!overlayEl) return;
    const rect = overlayEl.getBoundingClientRect();
    inkPointsRef.current.push({ x: clientX - rect.left, y: clientY - rect.top });
  }, []);

  const finalizeInkStroke = useCallback(() => {
    if (!inkDrawingRef.current || placementType !== 'ink') return;
    inkDrawingRef.current = false;

    const viewerPts = inkPointsRef.current;
    inkPointsRef.current = [];

    // Ignore trivial dots (require at least ~10px total path length)
    if (viewerPts.length < 2) return;
    let pathLen = 0;
    for (let i = 1; i < viewerPts.length; i++) {
      const dx = viewerPts[i].x - viewerPts[i - 1].x;
      const dy = viewerPts[i].y - viewerPts[i - 1].y;
      pathLen += Math.sqrt(dx * dx + dy * dy);
      if (pathLen >= 10) break;
    }
    if (pathLen < 10) return;

    // Light smoothing: reduce to at most 300 evenly sampled points
    const MAX_PTS = 300;
    let pts = viewerPts;
    if (pts.length > MAX_PTS) {
      const step = pts.length / MAX_PTS;
      pts = Array.from({ length: MAX_PTS }, (_, i) => pts[Math.round(i * step)] ?? pts[pts.length - 1]);
    }

    // Convert viewer-px points → PDF coords using the same transform used everywhere else
    const pdfPoints = pts.map((p) =>
      viewerToPdfCoords(p.x, p.y, overlayPageHeight, scale, overlayPageWidth, displayWidth, displayHeight)
    );

    onPlaceInk?.(pageIndex, pdfPoints, effectiveScaleX);
  }, [placementType, overlayPageHeight, overlayPageWidth, scale, displayWidth, displayHeight, effectiveScaleX, pageIndex, onPlaceInk]);

  // Window-level up/cancel listeners: finalize the stroke when the pointer is released
  // outside the overlay element. The overlay's onPointerMove (with pointer capture set
  // in handleInkPointerDown) is the SINGLE source for point collection — the window
  // pointermove and mousemove listeners previously duplicated every point because pointer
  // capture already delivers all move events to the overlay element.
  // FIX-2: removed window 'pointermove' and 'mousemove' — keep only up/cancel so
  // ink points are recorded exactly once per physical pointer movement.
  useEffect(() => {
    if (placementType !== 'ink') return;

    const onUp = () => finalizeInkStroke();
    const onMouseMove = (e: MouseEvent) => {
      if (!inkDrawingRef.current) return;
      appendInkPoint(e.clientX, e.clientY);
    };

    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [placementType, finalizeInkStroke, appendInkPoint]);

  const startInkStroke = useCallback(
    (clientX: number, clientY: number) => {
      if (placementType !== 'ink' || panMode || inkDrawingRef.current) return false;
      const overlayEl = overlayRef.current;
      if (!overlayEl) return false;
      inkDrawingRef.current = true;
      inkPointsRef.current = [];
      appendInkPoint(clientX, clientY);
      return true;
    },
    [placementType, panMode, appendInkPoint]
  );

  const handleInkPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (placementType !== 'ink' || panMode) return;
    // Only the primary button (left click / finger)
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    e.stopPropagation();
    if (!startInkStroke(e.clientX, e.clientY)) return;

    try {
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can fail in synthetic test environments — window listeners handle it.
    }
  }, [placementType, panMode, startInkStroke]);

  const handleInkMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (placementType !== 'ink' || panMode || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      startInkStroke(e.clientX, e.clientY);
    },
    [placementType, panMode, startInkStroke]
  );

  const handleInkPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!inkDrawingRef.current || placementType !== 'ink') return;
    appendInkPoint(e.clientX, e.clientY);
  }, [placementType, appendInkPoint]);

  const handleInkPointerUp = useCallback((_e: React.PointerEvent<HTMLDivElement>) => {
    finalizeInkStroke();
  }, [finalizeInkStroke]);

  // === ANNOTATION FIELD RENDERING ===
  // Returns an SVG element for annotation types (highlight, rect, ellipse, ink).
  // The SVG is absolutely positioned within the signing-field div, matching the
  // field's box exactly. For ink, PDF-coordinate points are converted to viewer px.
  function renderAnnotationSvg(field: SigningField, rect: { top: number; left: number; width: number; height: number }): React.ReactNode {
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    const svgBase: React.SVGProps<SVGSVGElement> = {
      width: w,
      height: h,
      style: { position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' },
      'aria-hidden': true,
    };

    if (field.type === 'highlight') {
      const fillColor = field.color ?? '#ffd54a';
      const opacity = field.opacity ?? 0.35;
      return (
        <svg {...svgBase}>
          <rect x={0} y={0} width={w} height={h} fill={fillColor} fillOpacity={opacity} />
        </svg>
      );
    }

    if (field.type === 'underline' || field.type === 'strikeout') {
      const stroke = field.strokeColor ?? field.color ?? (field.type === 'underline' ? '#2563eb' : '#e11d48');
      const sw = field.lineWidth ?? 2;
      const y = field.type === 'underline' ? Math.max(sw / 2, h - sw / 2) : h / 2;
      return (
        <svg {...svgBase}>
          <line x1={0} y1={y} x2={w} y2={y} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    }

    if (field.type === 'rect') {
      const stroke = field.strokeColor ?? field.color ?? '#e11d48';
      const sw = field.lineWidth ?? 2;
      const hasFill = !!field.fillColor;
      return (
        <svg {...svgBase}>
          <rect
            x={sw / 2}
            y={sw / 2}
            width={Math.max(0, w - sw)}
            height={Math.max(0, h - sw)}
            fill={hasFill ? (field.fillColor as string) : 'none'}
            fillOpacity={hasFill ? (field.opacity ?? 1) : 0}
            stroke={stroke}
            strokeWidth={sw}
          />
        </svg>
      );
    }

    if (field.type === 'ellipse') {
      const stroke = field.strokeColor ?? field.color ?? '#e11d48';
      const sw = field.lineWidth ?? 2;
      const cx = w / 2;
      const cy = h / 2;
      const rx = Math.max(0, cx - sw / 2);
      const ry = Math.max(0, cy - sw / 2);
      const hasFill = !!field.fillColor;
      return (
        <svg {...svgBase}>
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill={hasFill ? (field.fillColor as string) : 'none'}
            fillOpacity={hasFill ? (field.opacity ?? 1) : 0}
            stroke={stroke}
            strokeWidth={sw}
          />
        </svg>
      );
    }

    if (field.type === 'cross') {
      const stroke = field.strokeColor ?? field.color ?? '#dc2626';
      const sw = field.lineWidth ?? 3;
      const pad = Math.max(sw, 4);
      return (
        <svg {...svgBase}>
          <line x1={pad} y1={pad} x2={w - pad} y2={h - pad} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
          <line x1={w - pad} y1={pad} x2={pad} y2={h - pad} stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    }

    if (field.type === 'text-bubble') {
      const stroke = field.strokeColor ?? field.color ?? '#2563eb';
      const sw = field.lineWidth ?? 2;
      const fill = field.fillColor ?? '#eff6ff';
      const opacity = field.opacity ?? 0.92;
      const tailBaseY = Math.max(sw / 2, h - 16);
      return (
        <svg {...svgBase}>
          <rect
            x={sw / 2}
            y={sw / 2}
            width={Math.max(0, w - sw)}
            height={Math.max(0, h - sw - 10)}
            rx={Math.min(10, Math.max(3, h / 7))}
            fill={fill}
            fillOpacity={opacity}
            stroke={stroke}
            strokeWidth={sw}
          />
          <path
            d={`M ${Math.max(18, w * 0.22)} ${tailBaseY} L ${Math.max(8, w * 0.15)} ${h - sw / 2} L ${Math.max(34, w * 0.32)} ${tailBaseY}`}
            fill={fill}
            fillOpacity={opacity}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    if (field.type === 'ink' && field.points && field.points.length >= 2) {
      const stroke = field.strokeColor ?? field.color ?? '#0f172a';
      const sw = field.lineWidth ?? 2;

      // Convert PDF-coordinate points to viewer-px relative to this field's bounding box.
      // field.x/y is the bounding box origin (PDF coords). We map to screen coords then
      // subtract the field's screen left/top so the SVG origin is the top-left of the box.
      const screenPts = field.points.map((p) => {
        // px from top-left of the page overlay
        const vx = p.x * effectiveScaleX;
        const vy = (overlayPageHeight - p.y) * effectiveScaleY;
        // subtract the field box's screen origin
        return { x: vx - rect.left, y: vy - rect.top };
      });

      const pointsStr = screenPts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

      return (
        <svg {...svgBase}>
          <polyline
            points={pointsStr}
            fill="none"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    return null;
  }

  // NOTE: All keyboard handling (Delete/Backspace, arrows for nudge, Space pan, etc.)
  // has been centralized in SigningWorkspace for true global support that works
  // even when pages are virtualized/unmounted or focus is elsewhere.
  // Per-page listeners removed to eliminate duplicate firings and scope issues.

  const handleFloatingFormatApply = useCallback(
    (patch: TextFormatPatch) => {
      if (!selectedFieldId) return;
      const f = fields.find((x) => x.id === selectedFieldId);
      if (f && isTextBasedFieldType(f.type)) {
        onFieldUpdate(selectedFieldId, buildTextFormatUpdates(f, patch));
      }
    },
    [selectedFieldId, fields, onFieldUpdate]
  );

  const selectedFieldOnPage = selectedFieldId
    ? fields.find(f => f.id === selectedFieldId && f.pageIndex === pageIndex)
    : null;
  const floatingToolbarField = selectedFieldOnPage && isTextBasedFieldType(selectedFieldOnPage.type) && selectedFieldOnPage.id !== inlineEditingFieldId
    ? selectedFieldOnPage
    : null;

  // The floating toolbar is positioned with `position: fixed`, so it needs the
  // surface's viewport offset to translate the (surface-relative) field rect into
  // viewport coordinates. Reading surfaceRef.getBoundingClientRect() directly in the
  // render body forced a synchronous layout flush on *every* re-render — and the page
  // re-renders on every pointermove while a text field is dragged. Instead, capture the
  // offset in a layout effect when the toolbar (re)appears or page geometry changes.
  // Scroll/resize dismiss the toolbar via onDismiss (stale rect cannot track movement).
  const floatingToolbarFieldId = floatingToolbarField?.id ?? null;
  const [surfaceOffset, setSurfaceOffset] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [scrollDismissedFieldId, setScrollDismissedFieldId] = useState<string | null>(null);
  const scrollDismissRef = useRef<FloatingToolbarScrollDismissState | null>(null);
  const floatingToolbarShownAtRef = useRef(0);
  const previousSelectedFieldIdRef = useRef<string | null>(selectedFieldId);

  const previousSelectedFieldId = previousSelectedFieldIdRef.current;
  if (selectedFieldId !== previousSelectedFieldIdRef.current) {
    previousSelectedFieldIdRef.current = selectedFieldId;
  }

  const effectiveDismissedFieldId = effectiveScrollDismissedFieldId(
    selectedFieldId,
    scrollDismissedFieldId,
    previousSelectedFieldId,
  );

  const showFloatingToolbar = shouldShowFloatingToolbar({
    field: floatingToolbarField,
    inlineEditingFieldId,
    scrollDismissedFieldId: effectiveDismissedFieldId,
  });

  useLayoutEffect(() => {
    if (!floatingToolbarFieldId) return;
    const surfaceEl = surfaceRef.current;
    if (!surfaceEl) return;
    const rect = surfaceEl.getBoundingClientRect();
    setSurfaceOffset((prev) =>
      prev.left === rect.left && prev.top === rect.top ? prev : { left: rect.left, top: rect.top }
    );
  }, [floatingToolbarFieldId, effectiveActive, scale, displayWidth, displayHeight, nominalWidth, nominalHeight]);

  useLayoutEffect(() => {
    if (showFloatingToolbar && floatingToolbarFieldId) {
      floatingToolbarShownAtRef.current = performance.now();
    }
  }, [showFloatingToolbar, floatingToolbarFieldId]);

  useEffect(() => {
    if (!showFloatingToolbar || !floatingToolbarField) return;

    const container = document.querySelector('.editor-stage-viewer') as HTMLElement | null;
    if (!container) return;

    const sample = {
      scrollTop: container.scrollTop,
      scrollLeft: container.scrollLeft,
    };
    scrollDismissRef.current = armScrollDismissState(createScrollDismissState(sample), sample);

    const handleScroll = () => {
      const state = scrollDismissRef.current;
      if (!state) return;
      const scrollSample = {
        scrollTop: container.scrollTop,
        scrollLeft: container.scrollLeft,
      };
      const result = evaluateFloatingToolbarScrollDismiss(state, scrollSample, {
        shownAtMs: floatingToolbarShownAtRef.current,
        nowMs: performance.now(),
        graceMs: FLOATING_TOOLBAR_SCROLL_DISMISS_GRACE_MS,
      });
      scrollDismissRef.current = result.nextState;
      if (result.dismiss) {
        setScrollDismissedFieldId(floatingToolbarField.id);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      scrollDismissRef.current = null;
    };
  }, [showFloatingToolbar, floatingToolbarField]);

  return (
    <>
    <div 
      id={`pdf-page-${pageIndex}`} 
      className="pdf-page-wrapper" 
      style={{ width: 'fit-content' }}
    >
      {/* Virtualization: Only mount the expensive canvas + interactive overlays when the page is near the viewport.
          This is the highest-impact performance improvement for documents with many pages. */}
      {effectiveActive ? (
        <div
          ref={surfaceRef}
          className="pdf-page-surface"
          style={{
            width: nominalWidth,
            height: nominalHeight,
          }}
        >
          <canvas ref={canvasRef} className="pdf-page-canvas" />

          {/* Interactive field overlay — fills the same surface as the canvas */}
          {showPlacedFields && (
            <div
              ref={overlayRef}
              className="field-overlay"
              role="application"
              aria-label={`Page ${pageIndex + 1} interactive field overlay. ${isPlacing ? 'Click to place ' + (placementType || 'field') + '.' : 'Drag fields to move, use arrow keys to nudge selected.'}`}
              style={{
                cursor: placementType === 'ink' ? 'crosshair' : isPlacing ? 'crosshair' : 'default',
                pointerEvents: isLoadingSizes || panMode ? 'none' : 'auto',
                // Dim live fields during snapshot compare so ghost overlay reads clearly.
                opacity: isLoadingSizes ? 0.6 : compareActive ? 0.35 : 1,
              }}
              onClick={handleOverlayClick}
              onPointerDown={placementType === 'ink' ? handleInkPointerDown : undefined}
              onPointerMove={placementType === 'ink' ? handleInkPointerMove : undefined}
              onPointerUp={placementType === 'ink' ? handleInkPointerUp : undefined}
              onMouseDown={placementType === 'ink' ? handleInkMouseDown : undefined}
              tabIndex={-1}
            >
              {pageFields.map((field) => {
                const rect = getFieldScreenRect(field);
                const isEditing = inlineEditingFieldId === field.id;
                const isSelected = field.id === selectedFieldId;
                const isCheckboxField = field.type === 'checkbox';
                // Annotation types have their own SVG-based rendering
                const isAnnotation =
                  field.type === 'highlight' ||
                  field.type === 'underline' ||
                  field.type === 'strikeout' ||
                  field.type === 'rect' ||
                  field.type === 'ellipse' ||
                  field.type === 'cross' ||
                  field.type === 'text-bubble' ||
                  field.type === 'ink';
                // Ink fields don't support resize (their geometry is defined by points[])
                const supportsResize = !isAnnotation || field.type !== 'ink';

                // These field types now use exact server mirroring math for text positioning.
                // Force padding:0 so the box the user sees/resizes is identical to the box
                // the server centering math runs inside. This was a deeper source of the
                // consistent vertical drift on signature-type after Apply.
                const layoutMode = fieldTextLayoutMode(field.type, {
                  boxHeight: field.height,
                  value: field.value,
                });
                const usesPreciseTextLayout =
                  field.type === 'signature-type' || layoutMode === 'form-line';
                const isTextField = isTextBasedFieldType(field.type);
                const isAutoSizedText =
                  field.type === 'signature-type' ||
                  field.type === 'date' ||
                  field.type === 'name' ||
                  field.type === 'initials' ||
                  (field.type === 'text' && isSingleLineFormText(field.value));
                const isCompactField =
                  isAutoSizedText || rect.width < 72 || rect.height < 30;

                return (
                  <div
                    key={field.id}
                    data-field-id={field.id}
                    role="button"
                    tabIndex={0}
                    className={`signing-field ${isSelected ? 'selected' : ''} ${isEditing ? 'editing' : ''} ${isCompactField && !isAnnotation ? 'compact-field' : ''} ${isAnnotation ? `annotation-field annotation-${field.type}` : isCheckboxField ? 'checkbox-field' : field.imageDataUrl ? 'image-field' : 'text-field'} ${usesPreciseTextLayout && !isAnnotation ? 'form-line-field' : ''} ${isCheckboxField && field.isChecked ? 'checked' : ''}`}
                    // BUG-23: text cursor + title hint for inline-editable fields.
                    title={
                      isAnnotation
                        ? field.type.charAt(0).toUpperCase() + field.type.slice(1) + ' annotation'
                        : isInlineEditableField(field)
                        ? 'Double-click to edit'
                        : isCheckboxField
                        ? 'Click to toggle'
                        : undefined
                    }
                    style={{
                      left: rect.left,
                      top: rect.top,
                      width: rect.width,
                      height: rect.height,
                      padding: isTextField ? 0 : undefined,
                      // Annotations: transparent background so SVG content shows through
                      background: isAnnotation ? 'transparent' : undefined,
                      border: isAnnotation && !isSelected ? 'none' : undefined,
                      outline: isAnnotation && !isSelected ? 'none' : undefined,
                      // BUG-23: show text cursor for editable text fields so users discover dbl-click.
                      cursor: isPlacing
                        ? undefined
                        : isAnnotation
                        ? 'move'
                        : isInlineEditableField(field)
                        ? 'text'
                        : isCheckboxField
                        ? 'pointer'
                        : undefined,
                    }}
                    onPointerDown={(e) => {
                      // Ink fields need special drag handling: translate all points by the delta
                      if (isAnnotation && field.type === 'ink') {
                        if (isPlacing) return;
                        e.stopPropagation();
                        onFieldSelect(field.id);

                        const fieldScreenRect = getFieldScreenRect(field);
                        const offsetX = e.clientX - (overlayRef.current?.getBoundingClientRect().left ?? 0) - fieldScreenRect.left;
                        const offsetY = e.clientY - (overlayRef.current?.getBoundingClientRect().top ?? 0) - fieldScreenRect.top;
                        const pointerOriginX = e.clientX;
                        const pointerOriginY = e.clientY;
                        let dragging = false;
                        const origPoints = field.points ?? [];
                        const origX = field.x;
                        const origY = field.y;

                        let pendingUpdate: Partial<SigningField> | null = null;
                        let rafId: number | null = null;

                        const flush = () => {
                          rafId = null;
                          if (!pendingUpdate) return;
                          const next = pendingUpdate;
                          pendingUpdate = null;
                          onFieldUpdate(field.id, next);
                        };

                        const handleMove = (moveEvent: PointerEvent) => {
                          if (!dragging) {
                            const dx = Math.abs(moveEvent.clientX - pointerOriginX);
                            const dy = Math.abs(moveEvent.clientY - pointerOriginY);
                            if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
                            dragging = true;
                          }
                          const overlayRect = overlayRef.current?.getBoundingClientRect();
                          if (!overlayRect) return;
                          const vx = moveEvent.clientX - overlayRect.left - offsetX;
                          const vy = moveEvent.clientY - overlayRect.top - offsetY;
                          const pdfX = vx / effectiveScaleX;
                          const pdfY = overlayPageHeight - vy / effectiveScaleY - field.height;

                          // Clamp the bounding box to the page exactly like regular
                          // (non-ink) drags do via clampFieldToPage. clampFieldToPage
                          // only adjusts x/y (and size), so we re-derive the on-page
                          // delta from the clamped origin and apply that SAME delta to
                          // every stroke point. This keeps the polyline locked to its
                          // bounding box (the server anchors ink to field.x/y), so the
                          // box and stroke never drift apart and never leave the page.
                          const clamped = clampFieldToPage(
                            { ...field, x: pdfX, y: pdfY },
                            overlayPageWidth,
                            overlayPageHeight
                          );
                          const dPdfX = clamped.x - origX;
                          const dPdfY = clamped.y - origY;
                          const newPoints = origPoints.map((p) => ({
                            x: p.x + dPdfX,
                            y: p.y + dPdfY,
                          }));
                          pendingUpdate = {
                            x: clamped.x,
                            y: clamped.y,
                            points: newPoints,
                          };
                          if (rafId === null) {
                            rafId = requestAnimationFrame(flush);
                          }
                        };
                        const handleUp = () => {
                          window.removeEventListener('pointermove', handleMove);
                          window.removeEventListener('pointerup', handleUp);
                          if (rafId !== null) {
                            cancelAnimationFrame(rafId);
                            rafId = null;
                          }
                          flush();
                        };
                        window.addEventListener('pointermove', handleMove);
                        window.addEventListener('pointerup', handleUp);
                        return;
                      }
                      handleFieldPointerDown(e, field);
                    }}
                    onDoubleClick={() => handleFieldDoubleClick(field)}
                    onMouseDown={(e) => {
                      if (isCheckboxField && !isPlacing && e.button === 0) {
                        e.stopPropagation();
                        onFieldSelect(field.id);
                      }
                    }}
                    // BUG-06: checkbox click toggles isChecked (pointer-up so it fires after drag check).
                    // FIX-1: skip the toggle when the pointer actually dragged (didDragRef was set
                    // in handleFieldPointerDown's handleMove once the threshold was exceeded).
                    onClick={(e) => {
                      if (isCheckboxField && !isPlacing) {
                        // BUG-1: delete button is inside the checkbox; ignore clicks on it.
                        if ((e.target as HTMLElement).closest('.field-delete-btn')) return;
                        e.stopPropagation();
                        const draggedThisField =
                          didDragRef.current && pointerDownFieldIdRef.current === field.id;
                        didDragRef.current = false;
                        pointerDownFieldIdRef.current = null;
                        if (draggedThisField) return;
                        onFieldSelect(field.id);
                        onFieldUpdate(field.id, { isChecked: !field.isChecked });
                      }
                    }}
                    onKeyDown={(e) => {
                      // BUG-06: Enter/Space toggles checkbox when focused.
                      if (isCheckboxField && !isPlacing && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onFieldUpdate(field.id, { isChecked: !field.isChecked });
                        return;
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onFieldSelect(field.id);
                      }
                      // Delete/Backspace handled globally in SigningWorkspace (avoids duplicate
                      // firings and keeps behavior consistent when pages are virtualized).
                    }}
                    aria-label={`${field.type.replace(/-/g, ' ')} field${field.value ? `: ${field.value}` : ''} on page ${pageIndex + 1}${isSelected ? ', selected' : ''}. Drag or use arrows to move. Delete key removes.`}
                    aria-selected={isSelected}
                  >
                    {/* Annotation SVG rendering (highlight/rect/ellipse/cross/bubble/ink) */}
                    {isAnnotation && renderAnnotationSvg(field, rect)}

                    {field.type === 'text-bubble' && !isEditing && (
                      <div className="pointer-events-none absolute inset-x-2 top-1 bottom-3 overflow-hidden">
                        <FieldTextPreview
                          field={field}
                          boxWidthPx={Math.max(1, rect.width - 16)}
                          boxHeightPx={Math.max(1, rect.height - 16)}
                          horizontalScale={effectiveScaleX}
                          verticalScale={effectiveScaleY}
                          placeholder="Note"
                        />
                      </div>
                    )}

                    {/* Content rendering (hidden while inline editor is open; skipped for annotations except bubble text above) */}
                    {!isAnnotation && (
                      isEditing ? null : isCheckboxField ? null : field.imageDataUrl ? (
                        <SignatureImage
                          src={field.imageDataUrl}
                          containerWidth={rect.width}
                          containerHeight={rect.height}
                        />
                      ) : isTextField ? (
                        <FieldTextPreview
                          field={field}
                          boxWidthPx={rect.width}
                          boxHeightPx={rect.height}
                          horizontalScale={effectiveScaleX}
                          verticalScale={effectiveScaleY}
                          placeholder={
                            field.type === 'date'
                              ? 'Date'
                              : field.type === 'name'
                                ? 'Name'
                                : field.type === 'initials'
                                  ? 'Initials'
                                  : 'Text'
                          }
                        />
                      ) : null
                    )}

                    {/* "Saved" badge */}
                    {field.savedSignatureId && !isCheckboxField && !isAnnotation && (
                      <div
                        className="absolute -top-2 -left-2 text-[9px] leading-none px-1.5 py-0.5 rounded-full bg-state-success text-white font-semibold shadow-sm border border-white pointer-events-none select-none"
                        title="This signature was placed from your locally saved library"
                      >
                        💾
                      </div>
                    )}

                    {/* BUG-16: resize handle shown for ALL selected non-ink fields. */}
                    {isSelected && supportsResize && (
                      <div
                        className="resize-handle"
                        onPointerDown={(e) => handleResizePointerDown(e, field)}
                        aria-hidden="true"
                        title={
                          field.imageDataUrl
                            ? 'Drag to resize (hold Shift for free resize)'
                            : 'Drag to resize field'
                        }
                      />
                    )}

                    {/* Delete X */}
                    {isSelected && (
                      <div
                        role="button"
                        tabIndex={-1}
                        className="field-delete-btn"
                        onClick={(e) => handleFieldDelete(e, field.id)}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        title="Delete field"
                        aria-label="Remove field from page"
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFieldDelete(e as any, field.id); } }}
                      >
                        ×
                      </div>
                    )}
                  </div>
                );
              })}

              {inlineEditingFieldId && onCommitInlineEdit && onCancelInlineEdit && (() => {
                const editing = pageFields.find((f) => f.id === inlineEditingFieldId);
                if (!editing || !isInlineEditableField(editing)) return null;
                const rect = getFieldScreenRect(editing);
                return (
                  <InlineFieldEditor
                    key={`edit-${editing.id}`}
                    field={editing}
                    screenRect={rect}
                    scale={effectiveScaleY}
                    onCommit={(value, editorBox) => onCommitInlineEdit(editing.id, value, editorBox)}
                    onCancel={onCancelInlineEdit}
                    parentDismissRef={inlineEditDismissRef}
                  />
                );
              })()}
            </div>
          )}
        </div>
      ) : (
        // Lightweight placeholder that preserves exact scroll height when page is far away (virtualization)
        <div
          className="pdf-page-surface"
          style={{
            width: nominalWidth,
            height: nominalHeight,
            background: 'transparent',
          }}
        />
      )}

      {/* Page label is always visible (cheap) */}
      <div className="absolute -top-3 right-3 bg-[var(--bg-panel)] text-[10px] px-2 py-0.5 rounded border border-[var(--border-hairline)] text-[var(--text-muted)] shadow-sm">
        Page {pageIndex + 1}
      </div>

      {/* Ghost layer for snapshot comparison — only on active/nearby pages (virtualization).
          Rendering ghosts on all 12+ pages was a major compare-mode memory/CPU leak. */}
      {effectiveActive && compareFields && compareFields.length > 0 && (
        <div
          className="field-overlay pointer-events-none"
          style={{
            width: displayWidth,
            height: displayHeight,
            opacity: compareOpacity,
          }}
        >
          {/* BUG-19: only render ghosts that belong to THIS page. */}
          {compareFields.filter((field: any) => field.pageIndex === pageIndex).map((field: any) => {
            const rect = pdfToViewerCoords(
              field,
              overlayPageHeight,
              scale,
              overlayPageWidth,
              displayWidth,
              displayHeight
            );

            // Use the pre-computed status Map from PdfViewer when available —
            // O(1) lookup vs. the previous O(N) .find() per ghost per page.
            // Falls back to inline computation if the prop isn't provided
            // (e.g. older callers / tests).
            const preStatus = ghostStatusById?.get(field.id);
            let status: string;
            let titleText: string;
            if (preStatus) {
              status = preStatus.status;
              titleText =
                preStatus.status === 'removed'
                  ? 'Removed since snapshot'
                  : preStatus.status === 'changed'
                    ? 'Moved or resized since snapshot'
                    : 'Unchanged from snapshot';
            } else {
              const currentMatch = fields.find(f => f.id === field.id);
              status = 'unchanged';
              titleText = 'Unchanged from snapshot';
              if (!currentMatch) {
                status = 'removed';
                titleText = 'Removed since snapshot';
              } else {
                const tol = 0.6;
                const pos = Math.abs((field.x ?? 0) - currentMatch.x) > tol || Math.abs((field.y ?? 0) - currentMatch.y) > tol || Math.abs((field.width ?? 0) - currentMatch.width) > tol || Math.abs((field.height ?? 0) - currentMatch.height) > tol;
                const val = (field.value || '') !== (currentMatch.value || '') || (field.isChecked ?? false) !== (currentMatch.isChecked ?? false);
                if (pos || val) {
                  status = 'changed';
                  titleText = pos ? 'Moved or resized since snapshot' : 'Value changed since snapshot';
                }
              }
            }
            const isRem = status === 'removed';
            const isChg = status === 'changed';
            const outlineC = isRem ? '#ef4444' : isChg ? '#eab308' : '#64748b';
            const bgC = isRem ? 'rgba(239,68,68,0.13)' : isChg ? 'rgba(234,179,8,0.13)' : 'rgba(100,116,139,0.07)';
            const textC = isRem ? '#b91c1c' : isChg ? '#854d0e' : '#475569';
            const badge = isRem ? 'REMOVED' : isChg ? 'CHANGED' : '';

            return (
              <div
                key={`ghost-${field.id}`}
                className={`signing-field ${isRem ? 'ghost-removed' : isChg ? 'ghost-changed' : 'ghost-unchanged'}`}
                title={titleText}
                style={{
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                  outline: `1.5px dashed ${outlineC}`,
                  background: bgC,
                  pointerEvents: 'none',
                }}
              >
                {/* BUG-21: use resolveFieldTextStyle + cssFontStack to match live field fonts. */}
                {(() => {
                  // Ghost fields may be plain objects without all SigningField methods,
                  // but resolveFieldTextStyle only reads data properties.
                  const ghostStyle = resolveFieldTextStyle(field as SigningField);
                  const ghostCss = toPreviewCss(ghostStyle, effectiveScaleY);
                  return (
                    <span
                      style={{
                        fontFamily: cssFontStack(ghostStyle.fontFamily),
                        fontStyle: ghostCss.fontStyle,
                        fontWeight: ghostCss.fontWeight,
                        color: textC,
                        fontSize: ghostCss.fontSize,
                      }}
                    >
                      {field.value || (field.type === 'date' ? 'Date' : field.type === 'name' ? 'Name' : 'Text')}
                    </span>
                  );
                })()}
                {badge && (
                  <div
                    className="absolute -top-1.5 -right-1 text-[7px] leading-none px-1 py-px rounded font-bold tracking-wider border pointer-events-none select-none"
                    style={{ background: isRem ? '#fee2e2' : '#fef9c3', color: isRem ? '#991b1b' : '#713f12', borderColor: isRem ? '#fca5a5' : '#fde047' }}
                  >
                    {badge}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
      {showFloatingToolbar && floatingToolbarField && (() => {
        const rect = getFieldScreenRect(floatingToolbarField);
        // Surface viewport offset is cached in a layout effect (see surfaceOffset)
        // to avoid forcing a synchronous layout read on every drag-move re-render.
        const pageOffset = surfaceOffset;
        return (
          <FloatingTextFormatToolbar
            field={floatingToolbarField}
            screenRect={{
              left: rect.left + pageOffset.left,
              top: rect.top + pageOffset.top,
              width: rect.width,
              height: rect.height,
            }}
            fields={fields}
            bulkIds={[]}
            onApply={handleFloatingFormatApply}
          />
        );
      })()}
    </>
  );
};

export const PdfPage = React.memo(PdfPageComponent);

/**
 * Renders a signature image with explicit positioning using the exact same
 * contain + center algorithm as the server (containImageInBox).
 *
 * This is the proper, research-backed way to achieve reliable WYSIWYG for
 * placed signature images when using an external renderer (pdf-lib).
 *
 * Relying purely on CSS `object-fit: contain` + flex centering can produce
 * small differences due to subpixel rendering, box model edge cases, or
 * how browsers resolve replaced element sizes.
 */
function SignatureImage({
  src,
  containerWidth,
  containerHeight,
}: {
  src: string;
  containerWidth: number;
  containerHeight: number;
}) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [fit, setFit] = React.useState<{ x: number; y: number; width: number; height: number } | null>(null);

  React.useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const updateFit = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) return;

      const computed = containImageInBoxClient(
        img.naturalWidth,
        img.naturalHeight,
        containerWidth,
        containerHeight
      );
      setFit(computed);
    };

    if (img.complete) {
      updateFit();
    } else {
      img.addEventListener('load', updateFit, { once: true });
    }

    return () => {
      img.removeEventListener('load', updateFit);
    };
  }, [src, containerWidth, containerHeight]);

  // While we don't know the natural size yet, fall back to object-fit contain.
  // Once loaded, we switch to explicit absolute positioning for perfect match.
  const style: React.CSSProperties = fit
    ? {
        position: 'absolute',
        left: fit.x,
        top: fit.y,
        width: fit.width,
        height: fit.height,
        objectFit: 'contain',
        pointerEvents: 'none',
      }
    : {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        pointerEvents: 'none',
      };

  return (
    <img
      ref={imgRef}
      src={src}
      alt="signature"
      style={style}
      draggable={false}
    />
  );
}
